/**
 * Cursor-paginated storefront feed hook (TNK-124 phases D + E).
 *
 * Powers `/shop/inventory`'s buyer-facing grid. Each props change
 * (query / sort / filters) resets the loaded list and fetches a fresh
 * first page; `loadMore` advances the cursor for infinite scroll.
 *
 * Phase D shipped the cursor + infinite-scroll skeleton. Phase E moves
 * filters + sort server-side: every filter axis flows through to
 * `/api/store/inventory` so the buyer-facing grid never has to
 * substring-match the loaded subset.
 *
 * Caller is responsible for any debouncing on the `query` prop — see
 * `shop.inventory.tsx`'s 250ms debounced search input. The hook
 * coalesces back-to-back prop changes inside a single render via the
 * `fingerprint` memo so React's batching does the right thing.
 *
 * A `fetchToken` ref guards against stale-fetch races: when filters or
 * query change mid-fetch, the older request finishes but discards its
 * result instead of overwriting the newer page.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  fetchStorefrontFeed,
  type StorefrontCard,
  type StorefrontFeedFilters,
  type StorefrontSort,
} from "./storefront";

export type StorefrontFeedPhase =
  | { phase: "loading" }
  | {
      phase: "ready";
      cards: StorefrontCard[];
      hasMore: boolean;
      /** Total rows matching the active filter set (not just the loaded
       *  page). Drives the hero's "X of N matching" denominator. */
      total: number;
    }
  | { phase: "error" };

export interface UseStorefrontFeedArgs {
  query: string;
  sort: StorefrontSort;
  filters: StorefrontFeedFilters;
}

export interface UseStorefrontFeedResult {
  state: StorefrontFeedPhase;
  /** Trigger fetch of the next page. No-op when already loading, when
   *  no further pages exist, or when an error is currently in effect. */
  loadMore: () => void;
  /** True iff the next page is currently in-flight. Drives the
   *  "Loading more…" affordance under the grid. */
  loadingMore: boolean;
  /** True iff a fresh first page is in-flight after a filter / sort /
   *  query change. The grid stays visible during this — buyer keeps
   *  seeing the previous result set until the new one lands, instead
   *  of a blank flash that reads as a page reload. */
  refetching: boolean;
}

const PAGE_SIZE = 60;

export function useStorefrontFeed(
  args: UseStorefrontFeedArgs,
): UseStorefrontFeedResult {
  const [state, setState] = useState<StorefrontFeedPhase>({ phase: "loading" });
  const [loadingMore, setLoadingMore] = useState(false);
  const [refetching, setRefetching] = useState(false);

  // Args fingerprint — refire the first-page effect whenever any
  // semantic value changes, even when the parent passes a fresh object
  // reference with identical contents.
  const fingerprint = useMemo(() => JSON.stringify(args), [args]);

  // Latest args mirrored into a ref so loadMore picks up the current
  // filters without re-binding the IntersectionObserver in the consumer.
  const argsRef = useRef(args);
  argsRef.current = args;

  // Cursor for the most-recent loaded page. Reset on every fingerprint
  // change (next effect run starts the cursor over).
  const cursorRef = useRef<string | null>(null);
  const inFlightRef = useRef(false);
  /** Bumped on every fingerprint change so an in-flight fetch from a
   *  stale args set can detect it raced past and discard its result. */
  const fetchToken = useRef(0);

  useEffect(() => {
    const token = ++fetchToken.current;
    inFlightRef.current = true;
    setLoadingMore(false);
    cursorRef.current = null;
    // Stale-while-revalidate: keep the previous ready slice visible
    // until the new page lands. The `refetching` flag drives a subtle
    // dim on the grid so the buyer knows new results are coming
    // without the full blank-and-spin reload feel. Only blank to
    // "loading" when we don't yet have anything to show (initial
    // mount or recovery from error).
    setState((prev) => (prev.phase === "ready" ? prev : { phase: "loading" }));
    setRefetching(true);
    void (async () => {
      try {
        const page = await fetchStorefrontFeed({
          limit: PAGE_SIZE,
          q: args.query,
          sort: args.sort,
          filters: args.filters,
        });
        if (token !== fetchToken.current) return; // stale
        cursorRef.current = page.nextCursor;
        setState({
          phase: "ready",
          cards: page.cards,
          hasMore: page.nextCursor !== null,
          total: page.total,
        });
      } catch {
        if (token !== fetchToken.current) return;
        // On error after we already had a ready slice, stay on the
        // previous slice — the buyer can keep browsing what they had.
        // Only flip to the error screen if we had nothing.
        setState((prev) =>
          prev.phase === "ready" ? prev : { phase: "error" },
        );
      } finally {
        if (token === fetchToken.current) {
          inFlightRef.current = false;
          setRefetching(false);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fingerprint]);

  const loadMore = useCallback(async () => {
    if (inFlightRef.current) return;
    if (cursorRef.current === null) return;
    const token = fetchToken.current;
    const current = argsRef.current;
    inFlightRef.current = true;
    setLoadingMore(true);
    try {
      const page = await fetchStorefrontFeed({
        limit: PAGE_SIZE,
        cursor: cursorRef.current,
        q: current.query,
        sort: current.sort,
        filters: current.filters,
      });
      if (token !== fetchToken.current) return;
      cursorRef.current = page.nextCursor;
      setState((prev) =>
        prev.phase === "ready"
          ? {
              phase: "ready",
              cards: [...prev.cards, ...page.cards],
              hasMore: page.nextCursor !== null,
              total: page.total,
            }
          : prev,
      );
    } catch {
      // Keep the loaded cards on error — only the "load more" attempt
      // failed, the user can scroll/retry. The grid stays usable.
    } finally {
      inFlightRef.current = false;
      setLoadingMore(false);
    }
  }, []);

  return { state, loadMore, loadingMore, refetching };
}
